<template>
  <div class="template-create-container">
    <a-spin :spinning="pageLoading" tip="加载中..." class="page-spin">
      <a-row>
        <a-col :span="3" />
        <a-col :span="18">
          <div class="page-header">
            <span class="page-title">{{ pageType === 'update' ? '编辑' : '创建' }}模板</span>
            <span class="page-subtitle"
              >定义消息模板格式，支持 text、html、markdown、action_card 四种类型，变量采用 <code>{{ sintaxExample }}</code> 语法</span
            >
          </div>

          <a-row :gutter="24">
            <!-- 左侧：模板编辑区 -->
            <a-col :span="14">
              <a-card :bordered="false" class="form-card" title="模板配置">
                <div class="form-item">
                  <label class="form-label required">模板名称</label>
                  <a-input v-model="name" placeholder="如：订单发货通知模板" :maxLength="100" />
                </div>
                <div class="form-item">
                  <label class="form-label">模板描述</label>
                  <a-textarea v-model="description" placeholder="描述该模板的适用场景" :rows="2" />
                </div>
                <a-row :gutter="16">
                  <a-col :span="12">
                    <div class="form-item">
                      <label class="form-label required">模板类型</label>
                      <a-select v-model="site_id" :disabled="pageType === 'update' || pageType === 'show'" style="width: 100%">
                        <a-select-option value="text">text（纯文本）</a-select-option>
                        <a-select-option value="html">html（富文本）</a-select-option>
                        <a-select-option value="markdown">markdown（Markdown）</a-select-option>
                        <a-select-option value="action_card">action_card（卡片消息）</a-select-option>
                      </a-select>
                    </div>
                  </a-col>
                </a-row>
                <div class="form-item">
                  <label class="form-label required">模板标题</label>
                  <div class="editor-toolbar">
                    <span class="var-hint">可用变量：</span>
                    <a-tag v-for="v in availableVars" :key="v" color="blue" style="cursor: pointer" @click="insertVarToTitle(v)">
                      {{ displayVar(v) }}
                    </a-tag>
                  </div>
                  <a-input v-model="title" :placeholder="titlePlaceholder" :maxLength="100" />
                </div>
                <div class="form-item">
                  <label class="form-label">模板正文</label>
                  <div class="editor-toolbar">
                    <span class="var-hint">可用变量：</span>
                    <a-tag v-for="v in availableVars" :key="v" color="blue" style="cursor: pointer" @click="insertVar(v)">
                      {{ displayVar(v) }}
                    </a-tag>
                  </div>
                  <a-textarea v-model="content" :rows="site_id === 'html' ? 14 : 10" :placeholder="placeholderText" @input="onContentChange" />
                </div>
              </a-card>
            </a-col>

            <!-- 右侧：变量赋值 & 预览区 -->
            <a-col :span="10">
              <a-card :bordered="false" class="form-card preview-card" title="渲染预览">
                <template slot="extra">
                  <a-button size="small" type="primary" @click="renderTest" :loading="rendering"> <a-icon type="reload" /> 刷新预览 </a-button>
                </template>

                <!-- 变量赋值 -->
                <div v-if="parsedVars.length > 0" class="var-inputs">
                  <a-divider orientation="left" style="font-size: 13px">变量赋值</a-divider>
                  <div v-for="v in parsedVars" :key="v" class="var-input-item">
                    <span class="var-input-label">{{ displayVar(v) }}</span>
                    <a-input v-model="varValues[v]" size="small" :placeholder="varPlaceholders[v] || '请输入' + v" style="flex: 1" @change="debounceRender" />
                  </div>
                </div>

                <!-- 标题预览 -->
                <a-divider orientation="left" style="font-size: 13px">标题预览</a-divider>
                <div class="preview-title-box">
                  <div class="preview-title-content">{{ previewTitle || '（在标题中输入内容后预览）' }}</div>
                </div>

                <!-- 正文预览 -->
                <a-divider orientation="left" style="font-size: 13px">正文预览</a-divider>
                <div class="preview-content-box">
                  <!-- HTML 预览 -->
                  <div v-if="site_id === 'html'" class="ql-container ql-snow">
                    <div class="ql-editor" v-html="renderResult || '（在正文中输入内容后预览）'"></div>
                  </div>
                  <!-- Markdown 预览：渲染后展示，便于确认最终发出的样式 -->
                  <div v-else-if="site_id === 'markdown'" class="markdown-preview">
                    <message-content v-if="renderResult" type="markdown" :content="renderResult" />
                    <span v-else class="preview-placeholder">（在正文中输入内容后预览）</span>
                  </div>
                  <!-- action_card JSON 预览 -->
                  <div v-else-if="site_id === 'action_card'" class="markdown-preview">
                    <pre v-if="renderResult" class="markdown-text">{{ formattedCardJSON }}</pre>
                    <span v-else class="preview-placeholder">（在正文中输入内容后预览）</span>
                  </div>
                  <!-- Text 预览 -->
                  <div v-else class="text-preview">
                    <pre v-if="renderResult" class="text-content">{{ renderResult }}</pre>
                    <span v-else class="preview-placeholder">（在正文中输入内容后预览）</span>
                  </div>
                </div>
              </a-card>
            </a-col>
          </a-row>

          <!-- 操作按钮 -->
          <div class="step-actions">
            <a-button @click="$router.back()">取消</a-button>
            <a-button type="primary" @click="finishCreate" v-if="pageType !== 'show'" :loading="submitting" style="margin-left: 12px">
              <a-icon type="check" /> {{ pageType === 'update' ? '保存更新' : '创建模板' }}
            </a-button>
          </div>
        </a-col>
        <a-col :span="3" />
      </a-row>
    </a-spin>
  </div>
</template>

<script>
import { createTemplate, updateTemplate, getSingleTemplate, renderTemplate } from '@/api/notice'
import MessageContent from '@/components/MessageContent'

export default {
  components: { MessageContent },
  data() {
    return {
      submitting: false,
      rendering: false,
      pageLoading: false,
      name: '',
      title: '[{{.level}}][{{.system}}] 发出通知',
      description: '',
      content: `  日期/时间: {{.now}}
  事件ID: {{.id}}
  事件类型: {{.type_id}}
  严重性: {{.level}}
  系统: {{.system}}
  模块: {{.module}}
  部门: {{.department}}

{{.message}}`,
      site_id: 'text',
      renderResult: '',
      previewTitle: '',
      parsedVars: [],
      varValues: {},
      varPlaceholders: {
        now: '当前时间（自动填充）',
        level: '日志级别，如 INFO / WARN / ERROR / CRITICAL',
        system: '系统名称，如 erp / wms / noticegateway',
        module: '模块名称，如 order / inventory / user',
        department: '部门名称，如 研发一部 / 运营部',
        message: '通知正文内容',
        title: '自定义标题文本',
        type_id: '事件类型 ID（数字）',
        id: '事件日志 ID',
        create_time: '创建时间（自动填充）',
        update_time: '更新时间（自动填充）'
      },
      pageType: 'create',
      renderTimer: null
    }
  },
  computed: {
    availableVars() {
      return Object.keys(this.varPlaceholders)
    },
    placeholderText() {
      const examples = {
        text: `日期/时间: {{.now}}
事件ID: {{.id}}
事件类型: {{.type_id}}
严重性: {{.level}}
系统: {{.system}}
模块: {{.module}}
部门: {{.department}}

{{.message}}`,
        html: `<h3>{{.title}}</h3>
<p>时间: {{.now}}</p>
<p>事件ID: {{.id}}</p>
<p>级别: {{.level}}</p>
<p>系统: {{.system}}</p>
<p>模块: {{.module}}</p>
<p>部门: {{.department}}</p>
<p>{{.message}}</p>`,
        markdown: `## {{.title}}
- 时间: {{.now}}
- 事件ID: {{.id}}
- 事件类型: {{.type_id}}
- 级别: {{.level}}
- 系统: {{.system}}
- 模块: {{.module}}
- 部门: {{.department}}

{{.message}}`,
        action_card: `{
  "title": "{{.title}}",
  "markdown": "## {{.title}}\\n- 时间: {{.now}}\\n- 级别: {{.level}}\\n- 系统: {{.system}}\\n\\n{{.message}}",
  "single_title": "查看详情",
  "single_url": "https://xxx.com/detail"
}`
      }
      return examples[this.site_id] || ''
    },
    sintaxExample() {
      return '{{.变量名}}'
    },
    titlePlaceholder() {
      return '支持模板变量，如：[{{.level}}][{{.system}}] 发出通知'
    },
    formattedCardJSON() {
      if (!this.renderResult) return ''
      try {
        return JSON.stringify(JSON.parse(this.renderResult), null, 2)
      } catch {
        return this.renderResult
      }
    }
  },
  created() {
    this.pageType = this.$route.query.type
    this.id = this.$route.query.id
    if (this.pageType === 'update' || this.pageType === 'show') {
      this.pageLoading = true
      getSingleTemplate(this.id)
        .then(res => {
          const data = res.data
          this.name = data.name
          this.title = data.title
          this.description = data.description
          this.site_id = data.type
          this.content = data.content
          this.onContentChange()
        })
        .finally(() => {
          this.pageLoading = false
        })
    } else {
      this.onContentChange()
    }
  },
  methods: {
    displayVar(varName) {
      return '{{.' + varName + '}}'
    },
    onContentChange() {
      this.parseVariables()
    },
    parseVariables() {
      const fullText = this.title + ' ' + this.content
      const regex = /\{\{\.(\w+)\}\}/g
      const vars = new Set()
      let match
      while ((match = regex.exec(fullText)) !== null) {
        vars.add(match[1])
      }
      this.parsedVars = Array.from(vars)

      // 保留已有值，为新变量设置空值
      const newVarValues = {}
      this.parsedVars.forEach(v => {
        newVarValues[v] = this.varValues[v] || ''
      })
      this.varValues = newVarValues

      this.debounceRender()
    },
    insertVar(varName) {
      this.content += `{{.${varName}}}`
      this.onContentChange()
    },
    insertVarToTitle(varName) {
      this.title += `{{.${varName}}}`
      this.onContentChange()
    },
    debounceRender() {
      if (this.renderTimer) clearTimeout(this.renderTimer)
      this.renderTimer = setTimeout(() => {
        this.doRender()
      }, 300)
    },
    renderTest() {
      this.doRender()
    },
    doRender() {
      if (!this.content.trim()) {
        this.renderResult = ''
        return
      }
      this.rendering = true
      const params = {
        title: this.title,
        content: this.content,
        params: this.varValues
      }
      renderTemplate(params)
        .then(res => {
          this.previewTitle = res.data.title || ''
          this.renderResult = res.data.content || ''
        })
        .catch(() => {
          this.previewTitle = this.replaceVars(this.title)
          this.renderResult = this.replaceVars(this.content)
        })
        .finally(() => {
          this.rendering = false
        })
    },
    replaceVars(text) {
      if (!text) return ''
      let result = text
      this.parsedVars.forEach(v => {
        const val = this.varValues[v]
        const regex = new RegExp(`\\{\\{\\.${v}\\}\\}`, 'g')
        result = result.replace(regex, val || `{{.${v}}}`)
      })
      return result
    },
    finishCreate() {
      if (!this.name) {
        this.$message.warning('模板名称不能为空')
        return
      }
      if (!this.title) {
        this.$message.warning('模板标题不能为空')
        return
      }
      if (this.title.length > 100) {
        this.$message.warning('模板标题过长（最多100字符）')
        return
      }
      const params = {
        name: this.name,
        type: this.site_id,
        title: this.title,
        description: this.description,
        content: this.content
      }
      this.submitting = true
      const method = this.pageType === 'update' ? updateTemplate : createTemplate
      method(this.id, params)
        .then(() => {
          this.$message.success(`${this.pageType === 'update' ? '更新' : '创建'}成功`)
          this.$router.push({ name: 'TemplateManage' })
        })
        .catch(() => {
          this.$message.error(`${this.pageType === 'update' ? '更新' : '创建'}失败，请检查信息填写`)
        })
        .finally(() => {
          this.submitting = false
        })
    }
  }
}
</script>

<style scoped>
.template-create-container {
  padding: 24px;
  min-height: calc(100vh - 64px);
  background: #f0f2f5;
}

.page-spin >>> .ant-spin-container {
  width: 100%;
}

.page-header {
  margin-bottom: 24px;
  text-align: center;
}

.page-title {
  font-size: 22px;
  font-weight: 600;
  color: #1a1a2e;
  display: block;
}

.page-subtitle {
  font-size: 13px;
  color: #8c8c8c;
  margin-top: 4px;
  display: block;
}

.page-subtitle code {
  background: #e6f7ff;
  color: #1890ff;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 12px;
}

.form-card {
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  height: 100%;
}

.preview-card {
  position: sticky;
  top: 24px;
}

.form-item {
  margin-bottom: 18px;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.form-label.required::before {
  content: '*';
  color: #f5222d;
  margin-right: 4px;
}

.editor-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 6px;
  padding: 6px 10px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  min-height: 32px;
}

.var-hint {
  font-size: 12px;
  color: #8c8c8c;
  margin-right: 4px;
}

.no-var {
  font-size: 12px;
  color: #ccc;
}

.var-inputs {
  margin-bottom: 8px;
}

.var-input-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
}

.var-input-label {
  font-size: 12px;
  color: #1890ff;
  font-family: monospace;
  white-space: nowrap;
  min-width: 80px;
}

.preview-title-box {
  padding: 12px 14px;
  background: #f6f8fa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  min-height: 42px;
  margin-bottom: 12px;
}

.preview-title-content {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  word-break: break-all;
  line-height: 1.5;
}

.preview-content-box {
  min-height: 120px;
  max-height: 400px;
  overflow: auto;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #fff;
}

.preview-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80px;
  color: #ccc;
  font-size: 13px;
}

.text-preview,
.markdown-preview {
  padding: 12px;
}

.text-content,
.markdown-text {
  margin: 0;
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
  color: #333;
}

.step-actions {
  margin-top: 24px;
  text-align: center;
  padding: 16px 0;
}

.preview-mini-box {
  padding: 12px;
  background: #f6f8fa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  max-height: 200px;
  overflow: auto;
}

.preview-mini-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  word-break: break-all;
}

.preview-mini-content {
  font-size: 13px;
  color: #555;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: 'SFMono-Regular', Consolas, monospace;
}
</style>
